// React
import { useEffect, useState } from "react"

// Third-Party Libraries
import moment from "moment"

// Types
import type { SuccessFetch } from "types"
import type { FilterType } from "../../types"

// Utils
import { useApi } from "utils"

// Types
type FilterField = "date_from" | "date_end" | "purchase_order_id"
interface Type {
  bill_id: number
  bill_code: string
  transaction_date: string
  po_code: string
  approve_status: number
  location_name: string
}
interface FetchType extends Type {
  qty_delivered: string
  total_bill: string
}
interface DataType extends Type {
  qty_delivered: number
  total_bill: number
}

export function useList(): {
  data: DataType[]
  filter: FilterType
  isLoading: boolean
  setFilterValue: (
    field: FilterField,
    value: string
  ) => void
} {
  // Hooks
  const api = useApi()
  const [data, setData] = useState<DataType[]>([])
  const [filter, setFilter] = useState<FilterType>({
    date_from: moment().startOf("month").format("YYYY-MM-DD"),
    date_end: moment().format("YYYY-MM-DD"),
    purchase_order_id: ""
  })
  const [isLoading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    setLoading(true)

    api.get(
      "/probill/listapprovebill",
      {
        params: {
          ...filter,
          purchase_order_id: filter.purchase_order_id !== "" ? filter.purchase_order_id : undefined
        }
      }
    ).then((res: SuccessFetch<{
      payload: FetchType[]
    }>) => {
      setData(res.data.payload.map(item => {
        return {
          ...item,
          qty_delivered: parseInt(item.qty_delivered),
          total_bill: parseInt(item.total_bill)
        }
      }))
    }).catch(() => {
      setData([])
    }).finally(() => {
      setLoading(false)
    })

    return () => {
      setData([])
      setLoading(false)
    }

    // eslint-disable-next-line
  }, [filter])

  // Functions
  const setFilterValue = (
    field: FilterField,
    value: string
  ) => {
    setFilter(prev => {
      return {
        ...prev,
        purchase_order_id: field === "purchase_order_id" ? prev.purchase_order_id : "",
        [field]: value,
      }
    })
  }

  return { data, filter, isLoading, setFilterValue }
}